import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import type { NavigateFunction, Location } from 'react-router-dom';

import FormWizard from 'react-form-wizard-component';

import { getRoutesDirections } from '@/data/libraries/RoutesTranslated';
import { ConfigNumberParameters } from '@/data/libraries/ConfigParameters';
import { store } from '@/data/libraries/Store';
import { ShippingTypesCodes } from '@/data/libraries/ShippingTypes';

import { useResize } from '@/ui/hooks/useScreenSize';
import useReservation from '@/ui/hooks/useReservation';
import { useCreateCartClientDetailsForm } from '@/ui/hooks/useCreateCartClientDetailsForm';

import { Col, Row, Container } from 'react-bootstrap';
import { translate } from '@/domain/utils/translate';
import 'react-form-wizard-component/dist/style.css';
import '@/ui/components/formWizard/style.css';

import { useAppSelector, useAppDispatch } from '@/ui/store/helperRedux';
import { updateIndexCheckOut } from '@/ui/store/slices/indexCheckOutSlice';
import { RootState } from '@/ui/store/store';

import CartController from '@/controllers/CartController';
import BranchOfficesController from '@/controllers/BranchOfficesController';

import { ICart, ICartResponse } from '@/domain/interfaces/ICart';
import { IWebPayResponse } from '@/domain/interfaces/IPayment';
import { IBranchOffice } from '@/domain/interfaces/IBranchOffice';
import { IShippingTypes } from '@/domain/interfaces/IProduct';

import ShippingTypeLayout from '@/ui/components/sales/ShippingTypeLayout';
import PurchaseSummaryLayout from '@/ui/components/sales/PurchaseSummaryLayout';
import PaymentLayout from '@/ui/components/sales/PaymentLayout';
import ModalBlockedByCheckOut from '@/ui/components/modals/ModalBlockedByCheckOut';
import AlertModal from '@/ui/components/modals/AlertModal';


/** Componente CartCheckOut de Productos
 * @component
 */
const CartCheckOut = (): JSX.Element => {
  /** Hook para generar una navegación dentro de la aplicación. */
  const navigate: NavigateFunction = useNavigate();

  /** Hook actualizar states de redux */
  const dispatch = useAppDispatch();

  /**Trae la información del tipos de entrega desde Redux */
  const shippingTypesState: IShippingTypes[] = useAppSelector((state: RootState) => state.shippingTypes as IShippingTypes[]);

  /** Controlador de sucursales */
  const { getBranchOffices } = BranchOfficesController();

  /** Valor del ID de la tienda */
  const storeId: number = store.DEFAULT;

  /** Controlador de carrito */
  const { productQuantity,
    updateClientInCart,
    purchaseOrder,
    validateCart,
    updateCartProductsData,
    updateShippingTypeCost } = CartController();

  /** Hook para las reservas */
  const reservationHook = useReservation();

  /** Hook para trabajar con document location  */
  const location: Location = useLocation();

  /** Rutas URLs */
  const routesDirections = getRoutesDirections();

  /**Estado Redux del carrito */
  const cartProducts = useAppSelector((state: RootState) => state.cartState) as ICart;

  const [alertWarning, setAlertWarning] = useState<boolean>(false);
  const [alertAlert, setAlertAlert] = useState<boolean>(false);

  const hideAlertAlert = (): void => {
    setAlertAlert(false);
    setAlertWarning(false);
    navigate(routesDirections.CART_ROUTE, { state: { originURL: location.pathname } });
  };

  const hideAlertWarning = (): void => {
    setAlertAlert(false);
    setAlertWarning(false);
  };

  /** Inicialización del formulario */
  const formRef = useRef<ICart>((() => {
    // Crear una copia profunda del carrito
    const initialCart = JSON.parse(JSON.stringify(cartProducts));

    // Intentar cargar la identificación guardada en localStorage
    try {
      const savedIdentification = localStorage.getItem('checkout_identification');
      if (savedIdentification) {
        const identification = JSON.parse(savedIdentification);
        if (identification && identification.type) {
          initialCart.clientDetails.identificationId = identification;
        }
      }
    } catch (error) {
      // Si hay error al parsear, limpiar el localStorage para evitar errores futuros
      localStorage.removeItem('checkout_identification');
      // Continuar con el carrito sin la identificación guardada
    }

    return initialCart;
  })());

  // Sincronizar con cambios del carrito, pero mantener el tipo de documento
  useEffect(() => {
    // Crear una copia profunda del carrito actualizado
    const updatedCart = JSON.parse(JSON.stringify(cartProducts));

    // Preservar el tipo de documento y su valor si ya están establecidos
    if (formRef.current?.clientDetails?.identificationId?.type) {
      updatedCart.clientDetails.identificationId.type = formRef.current.clientDetails.identificationId.type;
      updatedCart.clientDetails.identificationId.value = formRef.current.clientDetails.identificationId.value;
    }

    // Actualizar el formRef con la copia actualizada
    formRef.current = updatedCart;
  }, [cartProducts]);

  // Limpiar localStorage cuando se finaliza el checkout o se navega fuera
  useEffect(() => {
    return () => {
      // Función de limpieza que se ejecuta cuando el componente se desmonta
      localStorage.removeItem('checkout_identification');
    };
  }, []);

  /**Estado Redux para mostrar modal bloqueo checkout */
  const showBlockedByCheckout: boolean = useAppSelector((state: RootState) => state.showModals.showBlockedByCheckout);
  const showBlockedInCheckout: boolean = useAppSelector((state: RootState) => state.showModals.showBlockedInCheckout);

  /** Referencia al Botón de ir Adelante */
  const nextButtonRef = useRef<HTMLButtonElement | null>(null);

  /** Referencia al formulario de Webpay */
  const webpayFormRef = useRef<HTMLFormElement | null>(null);

  /**Estado de la URL para Procesar Pago */
  const [url, setUrl] = useState<string>('');
  /** Estado del token para procesar pago */
  const [token, setToken] = useState<string>('');

  /**Función que actualiza el formulario */
  const updateFormRef = (formProduct: ICart): void => {
    formRef.current = formProduct;
  };

  /** Función para ir adelante */
  const handleNextButtonClick = (e: MouseEvent) => {
    e.preventDefault();

    nextButtonRef.current && nextButtonRef.current.click();
  };
  /**Función para finalizar el registro de producto base*/
  const handleFinishButtonClick = async (e: MouseEvent) => {
    e.preventDefault();
    await purchaseOrder(formRef.current).then((response: IWebPayResponse | number) => {
      if (typeof response === 'number') {
        if (response === 500) {
          setAlertAlert(true);
        } else {
          setAlertWarning(true);
        }
      } else if (response.tokenGateway && response.tokenGateway.length > 0) {
        setToken(response.tokenGateway);
        setUrl(response.urlGateway);
      }
    });
  };

  /**Función que chequea el estado de la reserva */
  const handleCheckReservation = async () => {
    const response: number = await reservationHook.checkReservation();
    if (response === 0) {
      handleDeleteReservation(true);
    }
  };

  /** Hook para traer el objeto vacío del cliente en el carrito */
  const emptyCartClientObject = useCreateCartClientDetailsForm();

  /**Función que elimina la reserva y oculta el modal, recibe un true o false para mostrar la alerta de inactividad */
  const handleDeleteReservation = async (showIdleModal: boolean): Promise<void> => {
    const isReservedDeleted: boolean = await reservationHook.deleteReservation();
    if (isReservedDeleted) {
      const updatedClientDetails = emptyCartClientObject;
      const updatedCartProducts = {
        ...cartProducts,
        clientDetails: updatedClientDetails,
      };
      updateClientInCart(updatedCartProducts.clientDetails);
      navigate(routesDirections.CART_ROUTE, { state: { originURL: location.pathname, showIdleModal } });
    }
  };

  /**UseEffect que envía a carrito si al entrar a la pagina no tengo id de reserva*/
  useEffect(() => {
    !cartProducts.reservationId && navigate(routesDirections.CART_ROUTE, { state: { originURL: location.pathname } });
  }, [cartProducts.reservationId]);

  /**UseEffect que envía a carrito si al entrar a la pagina hay problemas con el carrito  y no tengo id de reserva*/
  useLayoutEffect(() => {
    const responseValidation: ICartResponse = validateCart();
    responseValidation.error && handleDeleteReservation(false);
  }, [cartProducts]);

  /**UseEffect que envía a carrito si al entrar a la validación pagina el carrito esta vacío */
  useEffect(() => {
    productQuantity() === 0 && navigate(routesDirections.CART_ROUTE, { state: { originURL: location.pathname } });
  }, [productQuantity]);

  useEffect(() => {
    /** Verifica la reserva */
    handleCheckReservation();

    /** UseEffect que se desencadena a los 10 minutos para eliminar la reserva*/
    const timeoutId = setTimeout(() => {
      handleDeleteReservation(true);
    }, ConfigNumberParameters.RESERVATION_TIMEOUT_DURATION);

    return () => clearTimeout(timeoutId);
  }, []);

  /** State Index de checkout */
  const indexFormWizard: number = useAppSelector((state: RootState) => state.indexCheckOut.value);

  /** Actualiza el index del FormWizard */
  const tabChanged = ({ prevIndex, nextIndex }) => {
    if (indexFormWizard !== prevIndex) {
      dispatch(updateIndexCheckOut(prevIndex));
    }
    // Hacer scroll al top cuando se entra al resumen de compra (índice 1)
    if (nextIndex === 1) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  //UseEffect que envía el formulario oculto cuando recibe un Token
  useEffect(() => {
    if (token !== '') {
      dispatch(updateIndexCheckOut(0));
      webpayFormRef.current?.submit();
    }
  }, [token]);

  /** Actualiza datos de los productos del carrito */
  const cartRefresh = async (): Promise<void> => {
    await updateCartProductsData();
  };

  /**useEffect que actualiza los items del carrito */
  useLayoutEffect((): void => {
    cartRefresh();
  }, []);

  /**useEffect que actualiza los items del carrito */
  useLayoutEffect((): void => {
    updateFormRef(formRef.current);
  }, [formRef.current]);

  /** State que indica si la tienda solo permite retiro en tienda */
  const [onlyPickUp, setOnlyPickUp] = useState<boolean>(shippingTypesState.length === 1 && shippingTypesState[0].id == ShippingTypesCodes.PICK_UP);

  /** Se establece el valor del state onlyPickUp y los datos del formulario si aplica */
  useEffect(() => {
    if (shippingTypesState.length === 1 && shippingTypesState[0].id == ShippingTypesCodes.PICK_UP) {
      setOnlyPickUp(true);
      savePickUpAddress();
    } else {
      setOnlyPickUp(false);
    }
  }, [shippingTypesState])

  /** Componente a renderizar si no es necesario mostrar la selección de tipo de entrega */
  const HandleNextStep = (): JSX.Element => {
    useEffect(() => {
      nextButtonRef.current && nextButtonRef.current.click();
    }, [])
    return <></>;
  }

  /** Guarda dirección de sucursal si la tienda permite solo retiro en tienda */
  const savePickUpAddress = async (): Promise<void> => {

    updateShippingTypeCost(0);

    /** Almacena dirección de la sucursal */
    let branchOfficeData: string = translate('checkout.enabled_office');
    /** Consulta las sucursales de la tienda */
    const branchOfficesResponse: IBranchOffice[] = await getBranchOffices(storeId);

    if (branchOfficesResponse) {
      /** Copia de la data del formulario */
      const dataForm = JSON.parse(JSON.stringify(formRef.current));
      /** Actualiza los datos de retiro en tienda en el formulario */
      if (branchOfficesResponse.length > 0) {
        branchOfficeData = branchOfficesResponse[0].contact.address;
      }
      dataForm.clientDetails.shippingType = shippingTypesState.find((shippingType) => shippingType.id === ShippingTypesCodes.PICK_UP) ?? null;
      dataForm.clientDetails.pickupAddress = branchOfficeData;

      /** Genera las actualizaciones globales */
      updateFormRef(dataForm);
      updateClientInCart(formRef.current.clientDetails);
    }

  };

  /**CONSTANTES DE TEXTO */
  const SHIPPING_ADDRESS_TITLE: string = translate('checkout.shipment_title_text');
  const BOUGHT_RESUME_TITLE: string = translate('checkout.cart_resumen');
  const PAYMENT_TITLE: string = translate('checkout.pay');

  const WARNING_ALERT: string = translate('checkout.pay_warning');
  const ALERT_ALERT: string = translate('checkout.pay_alert');

  const fullSize: boolean = useResize() > 1740;

  return (
    <>
      <Container fluid className='p-0 bg-primary-5'>
        <Container className='p-0'>
          <Row className='g-0'>
            <Col className='mx-auto fw-white mt-5'>
              <FormWizard
                startIndex={indexFormWizard}
                onTabChange={tabChanged}
                onComplete={() => /*handleComplete(true)*/ null}
                backButtonTemplate={(handlePrevious: () => void) => (
                  <button className='d-none' onClick={handlePrevious}>
                    back
                  </button>
                )}
                nextButtonTemplate={(handleNext: () => void) => (
                  <button className='d-none' ref={nextButtonRef} onClick={handleNext}>
                    next
                  </button>
                )}
                finishButtonTemplate={(handleComplete: () => void) => (
                  <button className='d-none' onClick={handleComplete}>
                    finish
                  </button>
                )}>

                {/* Configurar Entrega */}
                <FormWizard.TabContent title={fullSize ? SHIPPING_ADDRESS_TITLE : ''}>
                  {!onlyPickUp && (
                    <ShippingTypeLayout
                      formRef={formRef}
                      updateFormRef={updateFormRef}
                      handleNextButton={(e: MouseEvent) => handleNextButtonClick(e)}
                    />
                  )}
                  {onlyPickUp && (
                    <HandleNextStep />
                  )}
                </FormWizard.TabContent>

                {/* Resumen de la compra */}
                <FormWizard.TabContent title={fullSize ? BOUGHT_RESUME_TITLE : ''}>
                  <PurchaseSummaryLayout
                    handleNextButton={(e: MouseEvent) => handleNextButtonClick(e)}
                  />
                </FormWizard.TabContent>

                {/* Pagar */}
                <FormWizard.TabContent title={fullSize ? PAYMENT_TITLE : ''}>
                  <PaymentLayout
                    formRef={formRef}
                    updateFormRef={updateFormRef}
                    handleNextButton={(e: MouseEvent) => handleFinishButtonClick(e)}
                  />
                  <form ref={webpayFormRef} action={url} method='POST' className='d-none'>
                    <input
                      type='text'
                      name='token_ws'
                      value={token}
                      onChange={() => null}
                    />
                  </form>
                </FormWizard.TabContent>
              </FormWizard>
            </Col>
          </Row>
        </Container>
      </Container>

      {/* Renderiza el modal de bloqueo fuera del FormWizard para mantener su funcionalidad */}
      {!showBlockedByCheckout && showBlockedInCheckout && (
        <ModalBlockedByCheckOut
          show={showBlockedInCheckout}
          handleSubmit={() => handleDeleteReservation(false)}
          handleCancel={() => window.location.reload()}
        />
      )}

      {alertAlert && (
        <AlertModal
          hideAlert={hideAlertAlert}
          type='danger'
          content={ALERT_ALERT}
          hide={15000}
        />
      )}
      {alertWarning && (
        <AlertModal
          hideAlert={hideAlertWarning}
          type='warning'
          content={WARNING_ALERT}
          hide={5000}
        />
      )}
    </>
  );
};

export default CartCheckOut;
